/**
 * ManualPostForm — "Add a post you made yourself" (BR-15). Lets the landlord record a post made by
 * hand (for example from a phone) so Resida can remind them to take it down later.
 *
 * With `site` set (a Marketing tab channel panel, listing-marketing-hub BR-11) the site is fixed: no
 * site picker, and the field is labelled with that site's name.
 */
import { useId, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiClientError } from '../../lib/api.core';
import {
  externalPostsKey,
  LISTING_SITE_LABELS,
  recordExternalPost,
  type ListingSiteDto,
} from '../../lib/external-listing-posts.api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

interface ManualPostFormProps {
  listingId: string;
  /** Fix the site (no picker) — the channel panel the form sits in. */
  site?: ListingSiteDto;
}

const SITES = Object.keys(LISTING_SITE_LABELS) as ListingSiteDto[];

export function ManualPostForm({ listingId, site: lockedSite }: ManualPostFormProps) {
  const queryClient = useQueryClient();
  const headingId = useId();
  const siteId = useId();
  const urlId = useId();
  const errorId = useId();
  const [pickedSite, setSite] = useState<ListingSiteDto>('facebook_marketplace');
  const site = lockedSite ?? pickedSite;
  const Heading = lockedSite ? 'h4' : 'h2';
  const [url, setUrl] = useState('');

  const record = useMutation({
    mutationFn: (submitted: { site: ListingSiteDto; url: string }) => recordExternalPost(listingId, submitted),
    onSuccess: () => {
      setUrl('');
      void queryClient.invalidateQueries({ queryKey: externalPostsKey(listingId) });
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    record.mutate({ site, url: url.trim() });
  };

  // The service rejects a link that is not a post on the chosen site with a 400 (INVALID_URL);
  // apiFetch surfaces the HTTP status but not the body's `code`, and the link is the only free-text
  // input, so every 400 is reported against it.
  const error = record.error;
  const errorMessage = !error
    ? null
    : error instanceof ApiClientError && error.status === 400
      ? `That link isn't a ${LISTING_SITE_LABELS[record.variables?.site ?? site]} post. Paste the link from the post's page.`
      : error instanceof ApiClientError && error.status === 409
        ? error.message
        : "Couldn't save the link. Try again.";

  return (
    <form aria-labelledby={headingId} onSubmit={handleSubmit} data-testid="manual-post-form" className="space-y-3">
      {/* In a channel step (h3) the form's own heading sits one level below it; standalone it is a section heading. */}
      <Heading id={headingId} className={lockedSite ? 'sr-only' : 'text-sm font-semibold text-foreground'}>
        {lockedSite ? `Record a ${LISTING_SITE_LABELS[lockedSite]} post` : 'Add a post you made yourself'}
      </Heading>
      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        {!lockedSite && (
          <div className="md:w-56">
            <label htmlFor={siteId} className="mb-1 block text-xs font-medium text-muted-foreground">
              Site
            </label>
            <select
              id={siteId}
              data-testid="manual-post-site"
              value={site}
              onChange={(e) => {
                setSite(e.target.value as ListingSiteDto);
                record.reset();
              }}
              className="h-11 w-full rounded-md border border-input bg-card px-3 text-base text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm"
            >
              {SITES.map((s) => (
                <option key={s} value={s}>
                  {LISTING_SITE_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="md:flex-1">
          <label htmlFor={urlId} className="mb-1 block text-xs font-medium text-muted-foreground">
            {lockedSite ? `${LISTING_SITE_LABELS[lockedSite]} post address` : 'Post link'}
          </label>
          <Input
            id={urlId}
            type="url"
            inputMode="url"
            placeholder="https://…"
            data-testid="manual-post-url"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              record.reset();
            }}
            aria-invalid={errorMessage ? true : undefined}
            aria-describedby={errorMessage ? errorId : undefined}
            className="h-11"
          />
        </div>
        <Button
          type="submit"
          variant="outline"
          disabled={record.isPending || !url.trim()}
          data-testid="manual-post-save"
          className="w-full md:w-auto"
        >
          {record.isPending ? 'Saving…' : lockedSite ? 'Add post' : 'Save'}
        </Button>
      </div>
      {errorMessage && (
        <p id={errorId} role="alert" data-testid="manual-post-error" className="text-sm text-destructive">
          {errorMessage}
        </p>
      )}
    </form>
  );
}
